import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, DatePicker, Flex, Select, Space, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";

const { RangePicker } = DatePicker;

export interface PeriodRange {
  from: Dayjs;
  to: Dayjs;
}

interface PeriodFilterProps {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
  showMonthControls?: boolean;
  showDayControls?: boolean;
  showRangeControls?: boolean;
  monthLabel?: string;
  /** Compact mode: all controls in a single horizontal row without labels. */
  compact?: boolean;
}

const monthsOptionsCount = 12;

const PeriodFilter = ({
  value,
  onChange,
  showMonthControls = true,
  showDayControls = true,
  showRangeControls = true,
  monthLabel = "Месяцы",
  compact = false,
}: PeriodFilterProps) => {
  const [monthOffset, setMonthOffset] = useState(0);
  const [dayOffset, setDayOffset] = useState(0);

  useEffect(() => {
    if (!value?.from || !value?.to) {
      return;
    }
    const dayDiff = value.to.diff(value.from, "day");
    if (dayDiff === 1 && value.from.startOf("day").isSame(value.from)) {
      const diff = value.from.startOf("day").diff(dayjs().startOf("day"), "day");
      setDayOffset(diff);
    }
    if (
      value.from.startOf("month").isSame(value.from) &&
      value.to.startOf("month").diff(value.from.startOf("month"), "month") === 1
    ) {
      const diff = value.from.startOf("month").diff(dayjs().startOf("month"), "month");
      setMonthOffset(diff);
    }
  }, [value]);

  const monthOptions = useMemo(() => {
    return Array.from({ length: monthsOptionsCount }).map((_, index) => {
      const offset = -index;
      const monthDate = dayjs().add(offset, "month");
      const label = monthDate.format("MMMM YYYY");
      return {
        label: label.charAt(0).toUpperCase() + label.slice(1),
        value: offset,
      };
    });
  }, []);

  const emitRange = (start: Dayjs, end: Dayjs) => {
    onChange({ from: start, to: end });
  };

  const applyMonth = (offset: number) => {
    const start = dayjs().add(offset, "month").startOf("month");
    const end = start.add(1, "month");
    setMonthOffset(offset);
    emitRange(start, end);
  };

  const applyDay = (offset: number) => {
    const start = dayjs().add(offset, "day").startOf("day");
    const end = start.add(1, "day");
    setDayOffset(offset);
    emitRange(start, end);
  };

  const handleDayPicker = (date: Dayjs | null) => {
    if (!date) {
      return;
    }
    const start = date.startOf("day");
    setDayOffset(start.startOf("day").diff(dayjs().startOf("day"), "day"));
    emitRange(start, start.add(1, "day"));
  };

  const handleRangePicker = (dates: null | (Dayjs | null)[]) => {
    if (!dates || !dates[0] || !dates[1]) {
      return;
    }
    emitRange(dates[0].startOf("day"), dates[1].startOf("day").add(1, "day"));
  };

  if (compact) {
    return (
      <Flex gap={8} wrap="wrap" align="center">
        {showDayControls && (
          <>
            <Button size="small" icon={<LeftOutlined />} onClick={() => applyDay(dayOffset - 1)} />
            <Button size="small" onClick={() => applyDay(0)}>
              Сегодня
            </Button>
            <Button size="small" icon={<RightOutlined />} onClick={() => applyDay(dayOffset + 1)} />
            <DatePicker
              size="small"
              value={value.from}
              onChange={handleDayPicker}
              format="DD.MM.YYYY"
            />
          </>
        )}
        {showRangeControls && (
          <RangePicker
            size="small"
            value={[value.from, value.to.subtract(1, "day")]}
            onChange={handleRangePicker}
            format="DD.MM.YYYY"
            style={{ width: 220 }}
          />
        )}
        {showMonthControls && (
          <Select
            size="small"
            value={monthOffset}
            onChange={(v) => applyMonth(v)}
            options={monthOptions}
            style={{ minWidth: 160 }}
          />
        )}
      </Flex>
    );
  }

  return (
    <Space orientation="vertical" size="small" style={{ width: "100%" }}>
      {showMonthControls && (
        <>
          {monthLabel && <Typography.Text strong>{monthLabel}</Typography.Text>}
          <Space wrap>
            <Button icon={<LeftOutlined />} onClick={() => applyMonth(monthOffset - 1)} />
            <Button onClick={() => applyMonth(0)}>Текущий</Button>
            <Button icon={<RightOutlined />} onClick={() => applyMonth(monthOffset + 1)} />
            <Select
              value={monthOffset}
              onChange={(v) => applyMonth(v)}
              options={monthOptions}
              style={{ minWidth: 200 }}
            />
          </Space>
        </>
      )}

      {showDayControls && (
        <>
          <Typography.Text strong>Дни</Typography.Text>
          <Space wrap>
            <Button icon={<LeftOutlined />} onClick={() => applyDay(dayOffset - 1)} />
            <Button onClick={() => applyDay(0)}>Сегодня</Button>
            <Button icon={<RightOutlined />} onClick={() => applyDay(dayOffset + 1)} />
            <DatePicker value={value.from} onChange={handleDayPicker} format="DD.MM.YYYY" />
          </Space>
        </>
      )}

      {showRangeControls && (
        <>
          <Typography.Text strong>Период</Typography.Text>
          <RangePicker
            value={[value.from, value.to.subtract(1, "day")]}
            onChange={handleRangePicker}
            format="DD.MM.YYYY"
            style={{ width: 240 }}
          />
        </>
      )}
    </Space>
  );
};

export default PeriodFilter;
